import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Booking, BookingStage } from "@/mocks/admin/bookings";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (booking: Booking) => void;
};

const moveTypes = [
  "Apartment — Studio",
  "Apartment — 1 BR",
  "Apartment — 2 BR",
  "Residential — 3 BR House",
  "Residential — 4 BR House",
  "Residential — 5 BR House",
  "Senior Move — 2 BR Condo",
  "Office Relocation",
  "Commercial — Warehouse",
  "Long Distance — 3 BR House",
];

const sourceOptions = [
  "Google Search",
  "Referral",
  "Yelp",
  "Instagram Ad",
  "Facebook Ad",
  "Website Form",
  "Phone Call",
  "Angi",
  "Repeat Customer",
];

const stageOptions: { key: BookingStage; label: string; color: string }[] = [
  { key: "new", label: "New Request", color: "bg-amber-500" },
  { key: "quoted", label: "Quoted", color: "bg-indigo-500" },
  { key: "confirmed", label: "Confirmed", color: "bg-brand-500" },
  { key: "scheduled", label: "Scheduled", color: "bg-emerald-500" },
];

const avatarColors = [
  "bg-brand-500",
  "bg-indigo-500",
  "bg-pink-500",
  "bg-emerald-600",
  "bg-fuchsia-500",
  "bg-amber-600",
  "bg-rose-500",
  "bg-teal-600",
  "bg-cyan-600",
  "bg-orange-600",
];

function pickInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatPreferredDate(iso: string) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "2-digit",
  });
}

function formatTime(t: string) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${m} ${period}`;
}

export default function AddBookingModal({ open, onClose, onCreate }: Props) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [customer, setCustomer] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState(sourceOptions[0]);
  const [moveType, setMoveType] = useState(moveTypes[2]);
  const [rooms, setRooms] = useState("");
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("09:00");
  const [fromCity, setFromCity] = useState("");
  const [toCity, setToCity] = useState("");
  const [estimatedQuote, setEstimatedQuote] = useState<string>("");
  const [depositPaid, setDepositPaid] = useState(false);
  const [stage, setStage] = useState<BookingStage>("new");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setError("");
      setSubmitting(false);
      setCustomer("");
      setEmail("");
      setPhone("");
      setSource(sourceOptions[0]);
      setMoveType(moveTypes[2]);
      setRooms("");
      setPreferredDate("");
      setPreferredTime("09:00");
      setFromCity("");
      setToCity("");
      setEstimatedQuote("");
      setDepositPaid(false);
      setStage("new");
      setNotes("");
    }
  }, [open]);

  if (!open) return null;

  const canNext1 = customer.trim() && phone.trim();
  const canNext2 = preferredDate && fromCity.trim() && toCity.trim();

  const handleSubmit = async () => {
    setError("");
    const quoteNum = estimatedQuote ? Number(estimatedQuote) : null;
    if (estimatedQuote && (Number.isNaN(quoteNum!) || quoteNum! < 0)) {
      setError("Please enter a valid quote amount or leave it blank.");
      return;
    }
    setSubmitting(true);

    const newId = "BK-" + (8000 + Math.floor(Math.random() * 2000));
    const initials = pickInitials(customer);
    const color = avatarColors[Math.floor(Math.random() * avatarColors.length)];
    const depositRequired = quoteNum ? Math.round(quoteNum * 0.2) : null;

    const { error: insertError } = await supabase.from("bookings").insert({
      id: newId,
      customer: customer.trim(),
      initials,
      color,
      email: email.trim() || null,
      phone: phone.trim(),
      move_type: moveType,
      preferred_date: formatPreferredDate(preferredDate),
      preferred_time: formatTime(preferredTime),
      from_city: fromCity.trim(),
      to_city: toCity.trim(),
      rooms: rooms.trim() || null,
      estimated_quote: quoteNum,
      deposit_required: depositRequired,
      deposit_paid: depositPaid,
      stage,
      source,
      submitted_at: "Just now",
      notes: notes.trim() || null,
    });

    if (insertError) {
      setError(insertError.message);
      setSubmitting(false);
      return;
    }

    const newBooking: Booking = {
      id: newId,
      customer: customer.trim(),
      initials,
      color,
      email: email.trim(),
      phone: phone.trim(),
      moveType,
      preferredDate: formatPreferredDate(preferredDate),
      preferredTime: formatTime(preferredTime),
      fromCity: fromCity.trim(),
      toCity: toCity.trim(),
      rooms,
      estimatedQuote: quoteNum,
      depositRequired,
      depositPaid,
      stage,
      source,
      submittedAt: "Just now",
      notes,
    };

    onCreate(newBooking);
    setSubmitting(false);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white w-full max-w-2xl rounded-lg border border-ink-100 max-h-[90vh] flex flex-col pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-indigo-100 text-indigo-700">
                <i className="ri-calendar-event-line text-lg"></i>
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-ink-900">New Booking Request</h2>
                <div className="text-xs text-ink-500">
                  Step {step} of 3 ·{" "}
                  {step === 1
                    ? "Customer info"
                    : step === 2
                    ? "Move details"
                    : "Quote & pipeline stage"}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          <div className="px-6 pt-4 pb-2 shrink-0">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s <= step ? "bg-indigo-500" : "bg-cream-100"
                  }`}
                ></div>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">
            {step === 1 && (
              <div className="space-y-4">
                <Field label="Customer name" required>
                  <input
                    type="text"
                    value={customer}
                    onChange={(e) => setCustomer(e.target.value)}
                    placeholder="e.g. Hannah Wallace"
                    className="form-input"
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Phone" required>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(512) 555-0142"
                      className="form-input"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="customer@email.com"
                      className="form-input"
                    />
                  </Field>
                </div>

                <Field label="How they found us">
                  <select
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    className="form-input pr-8"
                  >
                    {sourceOptions.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Notes from inquiry">
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                    placeholder="Special requests, fragile items, access issues, dogs at home…"
                    rows={3}
                    className="form-input resize-none"
                  />
                  <div className="text-[10px] text-ink-400 mt-1 text-right">
                    {notes.length}/500
                  </div>
                </Field>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <Field label="Move type" required>
                  <select
                    value={moveType}
                    onChange={(e) => setMoveType(e.target.value)}
                    className="form-input pr-8"
                  >
                    {moveTypes.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Rooms / size (optional)">
                  <input
                    type="text"
                    value={rooms}
                    onChange={(e) => setRooms(e.target.value)}
                    placeholder="e.g. 3 BR / 2 BA or 12 desks · server room"
                    className="form-input"
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Preferred date" required>
                    <input
                      type="date"
                      value={preferredDate}
                      onChange={(e) => setPreferredDate(e.target.value)}
                      className="form-input"
                    />
                  </Field>
                  <Field label="Preferred time">
                    <input
                      type="time"
                      value={preferredTime}
                      onChange={(e) => setPreferredTime(e.target.value)}
                      className="form-input"
                    />
                  </Field>
                </div>

                <Field label="From (origin city)" required>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-emerald-500">
                      <i className="ri-map-pin-2-line text-sm"></i>
                    </span>
                    <input
                      type="text"
                      value={fromCity}
                      onChange={(e) => setFromCity(e.target.value)}
                      placeholder="e.g. Austin, TX"
                      className="form-input pl-9"
                    />
                  </div>
                </Field>

                <Field label="To (destination city)" required>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-rose-500">
                      <i className="ri-flag-line text-sm"></i>
                    </span>
                    <input
                      type="text"
                      value={toCity}
                      onChange={(e) => setToCity(e.target.value)}
                      placeholder="e.g. Dripping Springs, TX"
                      className="form-input pl-9"
                    />
                  </div>
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <Field label="Estimated quote (optional)">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm">
                      $
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={10}
                      value={estimatedQuote}
                      onChange={(e) => setEstimatedQuote(e.target.value)}
                      placeholder="Leave blank if not quoted yet"
                      className="form-input pl-7"
                    />
                  </div>
                  {estimatedQuote && (
                    <div className="text-[11px] text-ink-500 mt-1">
                      Deposit (20%) will auto-calc to{" "}
                      <span className="font-bold text-ink-900">
                        ${Math.round(Number(estimatedQuote) * 0.2).toLocaleString()}
                      </span>
                    </div>
                  )}
                </Field>

                <Field label="Pipeline stage">
                  <div className="grid grid-cols-2 gap-2">
                    {stageOptions.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setStage(s.key)}
                        className={`text-sm font-semibold px-3 py-2.5 rounded-md border cursor-pointer whitespace-nowrap transition-colors flex items-center gap-2 ${
                          stage === s.key
                            ? "bg-ink-900 text-white border-ink-900"
                            : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${s.color}`}></span>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </Field>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={depositPaid}
                    onChange={(e) => setDepositPaid(e.target.checked)}
                    className="w-4 h-4 rounded border-ink-300 text-brand-500 cursor-pointer"
                  />
                  <span className="text-sm text-ink-700">
                    Deposit already collected from customer
                  </span>
                </label>

                {error && (
                  <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded-md">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{error}</span>
                  </div>
                )}

                <div className="bg-cream-50 border border-ink-100 rounded-md p-4">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500 mb-2">
                    Summary
                  </div>
                  <div className="text-sm text-ink-700 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-ink-500">Customer</span>
                      <span className="font-semibold">{customer || "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Move</span>
                      <span className="font-semibold truncate ml-3">{moveType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Route</span>
                      <span className="font-semibold truncate ml-3">
                        {fromCity || "—"} → {toCity || "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Date</span>
                      <span className="font-semibold">
                        {preferredDate ? formatPreferredDate(preferredDate) : "—"} ·{" "}
                        {formatTime(preferredTime)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-ink-100 bg-cream-50 flex items-center justify-between gap-2 shrink-0 rounded-b-lg">
            <button
              onClick={onClose}
              className="text-sm font-semibold text-ink-500 hover:text-ink-900 px-3 py-2 cursor-pointer"
            >
              Cancel
            </button>
            <div className="flex items-center gap-2">
              {step > 1 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold border border-ink-200 text-ink-700 hover:bg-white cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={step === 1 ? !canNext1 : !canNext2}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue
                  <i className="ri-arrow-right-line"></i>
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-indigo-500 hover:bg-indigo-600 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Saving…
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line"></i>
                      Create Booking
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}